import { z } from 'zod';
import { canUseModule } from '../roles-lite.js';
import { COST_GROUPS, COST_KEYS } from './cost.js';
// Oportunitats de negoci (§8.8): eina interna d'estudi de compres. Només per als agents a qui el
// superadmin ha concedit el mòdul. Una oportunitat la veuen qui la crea i aquells amb qui la comparteix.
/** Estat de l'oportunitat, que es mostra com un xip (decisió de l'usuari, 2026-10-30). */
export const OPPORTUNITY_STATUSES = ['open', 'pending', 'waitingOwner', 'negotiating', 'frozen', 'failed', 'acquired'];
/** Estats que ja no demanen feina: les llistes els compten a part (§10.1). */
export const CLOSED_OPPORTUNITY_STATUSES = ['failed', 'acquired'];
export const OPPORTUNITY_KINDS = ['flat', 'house', 'townHouse', 'chalet', 'rustic', 'plot', 'commercial', 'building', 'other'];
export const SELLER_KINDS = ['servicer', 'bank', 'private', 'agency', 'auction', 'developer', 'other'];
export const RISK_LEVELS = ['low', 'medium', 'high', 'veryHigh'];
export const NOTE_CATEGORIES = ['urban', 'legal', 'market', 'technical', 'visit', 'other'];
export const SCENARIO_STATUSES = ['draft', 'sent', 'countered', 'rejected', 'accepted'];
export const OPPORTUNITY_LEVELS = ['view', 'edit'];
export const OPPORTUNITY_LIMITS = {
    notes: 300,
    links: 300,
    scenarios: 30,
    files: 300,
    costLines: 40,
    noteChars: 8000,
    photoBytes: 8 * 1024 * 1024,
    docBytes: 25 * 1024 * 1024,
};
const id = z.string().regex(/^[A-Za-z0-9]{20}$/);
const shortText = (max) => z.string().trim().max(max);
const cents = z.number().int().min(0).max(100_000_000_000);
const pct = z.number().min(0).max(100);
export const costConfigSchema = z.object({
    taxMode: z.enum(['itp', 'vat', 'none']),
    itpBrackets: z.array(z.object({ upToCents: cents.nullable(), pct })).min(1).max(10),
    vatPct: pct,
    ajdPct: pct,
    lines: z.array(z.object({
        id: z.string().regex(/^[A-Za-z0-9_-]{1,40}$/),
        key: z.enum(COST_KEYS),
        group: z.enum(COST_GROUPS),
        label: shortText(80),
        kind: z.enum(['amount', 'pct']),
        cents,
        pct,
    })).max(OPPORTUNITY_LIMITS.costLines),
    contingencyPct: pct,
    holdingMonths: z.number().int().min(0).max(240),
    holdingMonthlyCents: cents,
    financing: z.object({ enabled: z.boolean(), loanPct: pct, ratePct: pct, openingFeePct: pct }),
});
export const exitConfigSchema = z.object({
    mode: z.enum(['none', 'sale', 'rent']),
    salePriceCents: cents,
    saleCostsPct: pct,
    monthlyRentCents: cents,
    annualExpensesCents: cents,
    vacancyPct: pct,
});
// ── Referències cadastrals ─────────────────────────────────────────
// Una o més per oportunitat (p. ex. una finca amb diverses parcel·les). 14 caràcters: la parcel·la;
// 20: un immoble concret dins la parcel·la (els 14 de la parcel·la + 4 del càrrec + 2 de control).
export const CADASTRAL_MAX = 10;
/** Majúscules, sense espais, guions ni punts: «0847106 VK4704F-0008 hp» → «0847106VK4704F0008HP». */
export function normalizeCadastralRef(value) {
    return value.toUpperCase().replace(/[\s.\-_/]/g, '');
}
export function isCadastralRef(value) {
    return /^[0-9A-Z]{14}([0-9A-Z]{6})?$/.test(value);
}
/**
 * Enllaç a la Sede Electrònica del Catastro: la llista de la parcel·la (els 14 primers caràcters,
 * partits en dues meitats). També per a una referència de 20, que hi surt dins la llista.
 * Comprovat a la seu el 2026-10-30. El de l'immoble sol (`OVCConCiud`) demana dades que no tenim.
 */
export function cadastreUrl(ref) {
    return `https://www1.sedecatastro.gob.es/CYCBienInmueble/OVCListaBienes.aspx?rc1=${ref.slice(0, 7)}&rc2=${ref.slice(7, 14)}`;
}
/** Dades generals de l'oportunitat (el que es desa amb el botó «Desa» de la fitxa). */
export const opportunityBaseSchema = z.object({
    title: z.string().trim().min(1).max(120),
    kind: z.enum(OPPORTUNITY_KINDS),
    areaM2: z.number().min(0).max(1_000_000).nullable(),
    plotM2: z.number().min(0).max(100_000_000).nullable(),
    location: z.object({
        address: shortText(200),
        municipality: shortText(80),
        geo: z.object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180) }).nullable(),
    }),
    askingPriceCents: cents.nullable(),
    cadastralRefs: z.array(z.string().transform(normalizeCadastralRef).refine(isCadastralRef)).max(CADASTRAL_MAX)
        .refine((list) => new Set(list).size === list.length),
    seller: z.object({ kind: z.enum(SELLER_KINDS).nullable(), name: shortText(120), contact: shortText(300) }),
    risk: z.object({ level: z.enum(RISK_LEVELS).nullable(), reasons: shortText(2000) }),
    cost: costConfigSchema,
    exit: exitConfigSchema,
});
export const statusNoteSchema = shortText(500);
export const createOpportunityInputSchema = z.object({ title: z.string().trim().min(1).max(120) });
/** `version`: la que tenia la pantalla. Si un altre l'ha desat mentrestant, es rebutja (no se sobreescriu). */
export const saveOpportunityInputSchema = z.object({ id, version: z.number().int().min(0), base: opportunityBaseSchema });
/** URL clicable: només http(s). Es desa tal com és, encara que amb el temps deixi de funcionar. */
export const linkUrlSchema = z.string().trim().max(2000).regex(/^https?:\/\/[^\s/?#.][^\s]*$/i);
const scenarioFields = {
    label: z.string().trim().min(1).max(80),
    offerCents: cents,
    status: z.enum(SCENARIO_STATUSES),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
    conditions: shortText(2000),
};
/** Les petites edicions (notes, enllaços, escenaris, fitxers): una per crida, sense tocar la resta. */
export const editOpportunityItemInputSchema = z.discriminatedUnion('op', [
    // L'estat va a part de la fitxa: es canvia amb un toc als xips, sense topar amb el que s'està editant.
    z.object({ op: z.literal('setStatus'), id, status: z.enum(OPPORTUNITY_STATUSES), statusNote: statusNoteSchema }),
    z.object({ op: z.literal('addNote'), id, category: z.enum(NOTE_CATEGORIES), text: z.string().trim().min(1).max(OPPORTUNITY_LIMITS.noteChars) }),
    z.object({ op: z.literal('updateNote'), id, itemId: id, category: z.enum(NOTE_CATEGORIES), text: z.string().trim().min(1).max(OPPORTUNITY_LIMITS.noteChars) }),
    z.object({ op: z.literal('deleteNote'), id, itemId: id }),
    z.object({ op: z.literal('addLink'), id, url: linkUrlSchema, title: shortText(200) }),
    z.object({ op: z.literal('deleteLink'), id, itemId: id }),
    z.object({ op: z.literal('addScenario'), id, ...scenarioFields }),
    z.object({ op: z.literal('updateScenario'), id, itemId: id, ...scenarioFields }),
    z.object({ op: z.literal('deleteScenario'), id, itemId: id }),
    z.object({ op: z.literal('updateFile'), id, itemId: id, description: shortText(300), favorite: z.boolean() }),
    z.object({ op: z.literal('deleteFile'), id, itemId: id }),
]);
export const shareOpportunityInputSchema = z.object({
    id,
    uid: z.string().min(1).max(128),
    level: z.enum(OPPORTUNITY_LEVELS).nullable(),
});
export const deleteOpportunityInputSchema = z.object({ id, confirmTitle: z.string() });
// ── Fitxers (fotos i documents) ──────────────────────────────────────
// Al bucket, en una carpeta tancada per regles (no la pública de les fotos de les cases):
//   foto:      opportunities/{oportunitat}/{fitxer}_{timestamp}_{480|1600}.{webp|jpg}
//   document:  opportunities/{oportunitat}/{fitxer}_{timestamp}.{ext}
export const OPPORTUNITY_PHOTO_WIDTHS = [480, 1600];
export const DOC_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'odt', 'ods', 'ppt', 'pptx', 'txt', 'csv', 'rtf', 'jpg', 'jpeg', 'png', 'webp', 'heic', 'gif', 'tif', 'tiff', 'zip', 'kml', 'kmz', 'dwg', 'dxf', 'eml', 'msg'];
export function docExtension(fileName) {
    const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
    return fileName.includes('.') && DOC_EXTENSIONS.includes(ext) ? ext : null;
}
export function opportunityPhotoPath(oppId, fileId, stamp, width, contentType) {
    return `opportunities/${oppId}/${fileId}_${stamp}_${width}.${contentType === 'image/webp' ? 'webp' : 'jpg'}`;
}
export function opportunityDocPath(oppId, fileId, stamp, ext) {
    return `opportunities/${oppId}/${fileId}_${stamp}.${ext}`;
}
const filePart = z.object({ path: z.string().max(300), contentType: z.string().max(120), bytes: z.number().int().min(1) });
export const addOpportunityFileInputSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('photo'), id, fileId: id, name: shortText(200), description: shortText(300), full: filePart, thumb: filePart }),
    z.object({ kind: z.literal('doc'), id, fileId: id, name: z.string().trim().min(1).max(200), description: shortText(300), file: filePart }),
]);
/** El camí és d'aquesta oportunitat i d'aquest fitxer, amb el format que exigeixen les regles. */
export function isOpportunityFilePathOf(path, oppId, fileId, kind) {
    const prefix = `opportunities/${oppId}/${fileId}_`;
    if (!path.startsWith(prefix))
        return false;
    const rest = path.slice(prefix.length);
    if (kind === 'doc') {
        const m = /^\d{13}\.([a-z0-9]{1,5})$/.exec(rest);
        return m !== null && DOC_EXTENSIONS.includes(m[1]);
    }
    return new RegExp(`^\\d{13}_${kind === 'thumb' ? 480 : 1600}\\.(webp|jpg)$`).test(rest);
}
/**
 * Accés a una oportunitat segons el perfil actiu (§8.8). Única font de veritat: la fan servir les
 * Functions i la interfície. Les regles en fan la mateixa comprovació per a la lectura.
 */
export function opportunityAccess(doc, uid, activeRole, modules) {
    if (activeRole === 'superadmin')
        return 'owner';
    if (!canUseModule(activeRole, modules, 'opportunities'))
        return null;
    if (doc.ownerId === uid)
        return 'owner';
    return doc.access[uid] ?? null;
}
export const canEditOpportunity = (a) => a === 'owner' || a === 'edit';
/** `access` → `accessUids` (amb el propietari), per a les regles i el `where` de les llistes (§20.1). */
export function opportunityAccessUids(ownerId, access) {
    return [ownerId, ...Object.keys(access).filter((u) => u !== ownerId).sort()];
}
/** Els favorits primer, i després els més nous. */
export function sortFiles(files) {
    return [...files].sort((a, b) => Number(b.favorite) - Number(a.favorite) || b.at - a.at);
}
